import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import API from '../utils/apiconfig';
import TablePagination from '../components/TablePagination';
import { ScrollableListBody } from '../components/ScrollableTable';
import { DEFAULT_PAGE_SIZE, parsePaginatedResponse } from '../utils/pagination';

const STATUS_STYLES = {
  ongoing: 'bg-blue-50 text-blue-700 border-blue-200',
  ended: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
};

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('en-IN');
}

function UserPlansHistory() {
  const [plans, setPlans] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState(null);
  const snackTimeoutRef = useRef(null);

  const showSnack = useCallback((message, type = 'error') => {
    if (snackTimeoutRef.current) {
      clearTimeout(snackTimeoutRef.current);
    }
    setSnack({ message, type });
    snackTimeoutRef.current = setTimeout(() => setSnack(null), 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (snackTimeoutRef.current) {
        clearTimeout(snackTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadHistory = async () => {
      setLoading(true);
      try {
        const response = await api.get(API.PLANS_HISTORY, {
          params: { page, page_size: pageSize },
          signal: controller.signal,
        });

        if (response.data?.success === false) {
          showSnack(response.data?.message || 'Failed to load plans history.');
          setPlans([]);
          setTotalPages(1);
          setTotalCount(0);
          return;
        }

        const parsed = parsePaginatedResponse(response.data, ['results', 'plans']);
        setPlans(parsed.items);
        setTotalPages(parsed.totalPages);
        setTotalCount(parsed.count);
        setPage(parsed.page);
      } catch (err) {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;

        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          'Failed to load plans history. Please try again.';
        showSnack(message);
        setPlans([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadHistory();
    return () => controller.abort();
  }, [page, pageSize, showSnack]);

  return (
    <div className="w-full min-h-[calc(100vh-4rem)] bg-transparent px-4 py-6 sm:px-5 sm:py-8">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="rounded-3xl bg-white border border-slate-200/80 p-5 sm:p-6 shadow-[0_12px_40px_-8px_rgba(15,23,42,0.12),0_4px_16px_-4px_rgba(15,23,42,0.08)]">
          <p className="text-[11px] font-bold tracking-[0.2em] text-indigo-600 uppercase mb-2">History</p>
          <h1
            className="text-2xl sm:text-3xl font-black text-slate-900"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
          >
            User Plans History
          </h1>
          <p className="text-sm text-slate-500 mt-1.5">
            View plan duration, paid amount, remaining amount, and current plan status.
          </p>
        </div>

        {!loading && plans.length === 0 ? (
          <div className="rounded-3xl bg-white border border-slate-200/80 p-6 sm:p-8 text-center shadow-[0_10px_30px_-12px_rgba(15,23,42,0.14)]">
            <h2
              className="text-2xl sm:text-3xl font-black text-slate-900"
              style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
            >
              No results
            </h2>
            <p className="text-sm text-slate-500 mt-2">No plan history to display right now.</p>
          </div>
        ) : !loading ? (
          <>
            <ScrollableListBody className="space-y-3" listStripe={false}>
              {plans.map((plan) => {
                const statusKey = String(plan.status || '').toLowerCase();
                const statusStyle =
                  STATUS_STYLES[statusKey] || 'bg-slate-50 text-slate-700 border-slate-200';
                const statusLabel = plan.status_label || statusKey;

                return (
                  <div
                    key={plan.id}
                    className="rounded-2xl bg-white border border-slate-200/80 p-4 sm:p-5 shadow-[0_8px_24px_-14px_rgba(15,23,42,0.24)]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs text-slate-500">Plan Name</p>
                        <h3 className="text-xl font-black text-slate-900">{plan.name}</h3>
                      </div>
                      <span
                        className={`text-xs font-bold px-2.5 py-1 rounded-full border capitalize ${statusStyle}`}
                      >
                        {statusLabel}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                        <p className="text-xs text-slate-500">Duration</p>
                        <p className="text-base font-black text-slate-900">{plan.duration}</p>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                        <p className="text-xs text-slate-500">Total Amount</p>
                        <p className="text-base font-black text-slate-900">
                          ₹{formatCurrency(plan.amount)}
                        </p>
                      </div>
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-3">
                        <p className="text-xs text-emerald-700">Paid</p>
                        <p className="text-base font-black text-emerald-700">
                          ₹{formatCurrency(plan.paid)}
                        </p>
                      </div>
                      <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3">
                        <p className="text-xs text-amber-700">Remaining</p>
                        <p className="text-base font-black text-amber-700">
                          ₹{formatCurrency(plan.remaining_amount)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs text-slate-500">
                      <span>
                        <strong className="text-slate-700">Plan start date:</strong>{' '}
                        {plan.start_date || '-'}
                      </span>
                      <span className="hidden sm:inline text-slate-300">|</span>
                      <span>
                        <strong className="text-slate-700">Completion date:</strong>{' '}
                        {plan.completion_date || plan.end_date || '-'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </ScrollableListBody>
            <TablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(next) => { setPageSize(next); setPage(1); }}
              disabled={loading}
              count={totalCount}
              className="shadow-[0_8px_24px_-14px_rgba(15,23,42,0.18)] rounded-2xl border-slate-200/80"
            />
          </>
        ) : null}
      </div>

      {snack && (
        <div
          className={`fixed bottom-6 right-6 z-50 rounded-xl px-4 py-3 text-sm font-semibold shadow-xl max-w-sm ${
            snack.type === 'warning'
              ? 'bg-amber-100 text-amber-900 border border-amber-300'
              : 'bg-red-100 text-red-900 border border-red-300'
          }`}
          role="alert"
        >
          {snack.message}
        </div>
      )}
    </div>
  );
}

export default UserPlansHistory;
